import * as React from 'react';
import { observer } from 'mobx-react';
import { SidePanelListType } from 'typings/enums';
import ManagerCardWorkflowStatus from 'nomst/Components/Decision/ManagerListSidePanel/managerCardWorkflowStatus';
import ManagerCardOverBudget from 'nomst/Components/Decision/ManagerListSidePanel/managerCardOverBudget';
import ManagerListSidePanelHeader from
    'nomst/Components/Decision/ManagerListSidePanel/managerListSidePanelHeader';
import SidePanelToggleButton from 'nomst/Components/Decision/ManagerListSidePanel/sidePanelToggleButton';
import { managerListSidePanelStore } from 'utils/MobxDataManager/singletons';
import { NavigationStore } from 'nomst/Stores/Navigation/navigation.store';
import { DecisionDispatcher } from 'nomst/Stores/Decision/decision.dispatcher';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { workflowStatusFromListTypeMap } from 'nomst/Stores/Decision/Constants/workflowStatusMap';
import { ManagerWorkflowStore } from 'nomst/Stores/Decision/managerWorkflow.store';
import './managerListSidePanel.scss';

export interface IManagerListSidePanelProps {
    decisionDispatcher: Nullable<DecisionDispatcher>;
    navigationStore: Nullable<NavigationStore>;
    configuration: Configuration;
    budgetNamesMap: Nullable<Map<number, string>>;
    managerWorkflowStore: ManagerWorkflowStore;
}

export class ManagerListSidePanel extends React.Component<IManagerListSidePanelProps> {
    render(): React.ReactNode {
        if (!managerListSidePanelStore.sidePanelIsOpen) {
            return this.renderSidePanelToggleButton();
        }

        return (
            <div className="nomst-manager-list-side-panel">
                {this.renderSidePanelToggleButton()}
                {this.renderHeader()}
                <div className="manager-list-wrapper">
                    {this.renderManagerList()}
                </div>
            </div>
        );
    }

    private renderSidePanelToggleButton(): React.ReactNode {
        const { decisionDispatcher } = this.props;
        if (managerListSidePanelStore.isFirstLevelManager) {
            return null;
        }
        return (
            <SidePanelToggleButton
                decisionDispatcher={decisionDispatcher}
                sidePanelIsOpen={managerListSidePanelStore.sidePanelIsOpen}
            />
        );
    }

    private renderHeader(): React.ReactNode {
        const { navigationStore, configuration, budgetNamesMap } = this.props;
        const workflowStatusType = managerListSidePanelStore.sidePanelListType === SidePanelListType.WorkflowStatus
            ? managerListSidePanelStore.workflowStatusType
            : null;
        const budgetCycleId = managerListSidePanelStore.sidePanelListType === SidePanelListType.WorkflowStatus
            ? null
            : managerListSidePanelStore.budgetCycleId;
        const budgetNames = managerListSidePanelStore.sidePanelListType === SidePanelListType.WorkflowStatus
            ? null
            : budgetNamesMap;
        return (
            <ManagerListSidePanelHeader
                navigationStore={navigationStore ?? {} as NavigationStore}
                sidePanelListType={managerListSidePanelStore.sidePanelListType}
                budgetCycleId={budgetCycleId}
                workflowStatusType={workflowStatusType}
                configuration={configuration}
                budgetNamesMap={budgetNames}
            />
        );
    }

    private renderManagerList(): React.ReactNode {
        const { navigationStore, configuration } = this.props;

        if (managerListSidePanelStore.sidePanelListType === SidePanelListType.WorkflowStatus) {
            const workflowStatus = workflowStatusFromListTypeMap.get(managerListSidePanelStore.workflowStatusType);
            if (!workflowStatus) {
                return null;
            }

            let managerCardWorkflowStatuses: IManagerCard[] =
                managerListSidePanelStore.managerCardsContainer.getWorkflowStatusManagers(workflowStatus);
            managerCardWorkflowStatuses = this.getFilteredManagerCards(managerCardWorkflowStatuses);

            return managerCardWorkflowStatuses ? managerCardWorkflowStatuses.map(managerCardWorkflowStatus => (
                <ManagerCardWorkflowStatus
                    key={managerCardWorkflowStatus.managerSourceId}
                    navigationStore={navigationStore}
                    managerCardWorkflowStatus={managerCardWorkflowStatus}
                    isManagerSelected={managerListSidePanelStore.isManagerSelected(
                        managerCardWorkflowStatus.managerSourceId)}
                    workflowStatusType={managerListSidePanelStore.workflowStatusType}
                    configuration={configuration}
                    currentReviewer={this.getCurrentReviewerDisplayName(managerCardWorkflowStatus.managerSourceId)}
                />
            )) : null;
        } else if (managerListSidePanelStore.sidePanelListType === SidePanelListType.OverBudget) {
            const budgetCycleId = managerListSidePanelStore.budgetCycleId;
            if (!budgetCycleId) {
                return null;
            }

            let managerCardOverBudgets: IManagerCardOverBudget[] = managerListSidePanelStore.managerCardsContainer
                .getOverBudgetManagers(budgetCycleId);
            managerCardOverBudgets = this.getFilteredManagerCards(managerCardOverBudgets) as IManagerCardOverBudget[];

            return managerCardOverBudgets ? managerCardOverBudgets.map(managerCardOverBudget => (
                <ManagerCardOverBudget
                    key={managerCardOverBudget.managerSourceId}
                    navigationStore={navigationStore ?? {} as NavigationStore}
                    managerCardOverBudget={managerCardOverBudget}
                    isManagerSelected={managerListSidePanelStore.isManagerSelected(
                        managerCardOverBudget.managerSourceId)}
                    budgetCycleId={managerCardOverBudget.budgetCycleId}
                    configuration={configuration}
                    currentReviewer={this.getCurrentReviewerDisplayName(managerCardOverBudget.managerSourceId)}
                />
            )) : null;
        }

        return null;
    }

    private getFilteredManagerCards(
        managerCards: Array<IManagerCard | IManagerCardOverBudget>
    ): Array<IManagerCard | IManagerCardOverBudget> {
        if (!managerListSidePanelStore.assignedToMeOnly) {
            return managerCards;
        }

        const filteredIds = new Set<string>(
            managerListSidePanelStore.getFilteredManagerIds(managerCards.map(mgr => mgr.managerSourceId))
        );
        return managerCards.filter(mgr => filteredIds.has(mgr.managerSourceId));
    }

    private getCurrentReviewerDisplayName(managerSourceId: string): string {
        const { managerWorkflowStore } = this.props;
        const workflow = managerWorkflowStore.getManagerWorkflow(managerSourceId);
        return workflow ? workflow.currentReviewerDisplayName : '';
    }
}

export default observer(ManagerListSidePanel);